import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Container,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock,
  Login as LoginIcon,
  Email,
  Security,
  ArrowForward,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import './../Login.css';
import config from '../config';
import etiStaakam from '../assets/etiStaakam.png';
import blue_rock_icon from '../assets/blue_rock_icon.png';
import PaperTrade_logo from '../assets/PaperTrade_logo.png';
import learnFNO_small_t from '../assets/learnFNO_small_t.png';
import money_mingle from '../assets/moneyMingle.png';
import triqutra_small from '../assets/Triqutra_small.png';
import suryanx_icon from '../assets/suryanx_icon.png';

import { getToken, storeToken, removeToken } from '../utils/auth';


const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hostnamee, setHostname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const { isDarkMode } = useTheme();

  //const { login } = useAuth();
  //const token = getToken();
  const token = localStorage.getItem('authToken');

  // console.log("login token", token);
  
  const navigate = useNavigate();

//   const handleLogin = (token, clientId) => {
//     login(token, clientId);
//     navigate('/');
//   };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');

    const data = {
      username: username,
      password: password,
    };

    try {
      const response = await fetch(`${config.API_BASE_URL}login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      
        const resData = await response.json();
        if (resData.success) {
          //alert('Login successful!');
          
          storeToken(resData);
          
          console.log('Login - Role from response:', resData.role);
          console.log('Login - All localStorage after login:', localStorage);

          if(resData.initSetupDone==0){
            navigate('/change-password');
          }

          // Role-based routing
          const role = resData.role;
          switch(role) {
            case 'A': // Admin
              navigate('/admin/dashboard');
              break;
            case 'M': // Master
              navigate('/master/dashboard');
              break;
            case 'B': // Bank
              navigate('/bank/dashboard');
              break;
            case 'BR': // Broker
              navigate('/broker/dashboard');
              break;
            case 'E': // End User (default)
            default:
              navigate('/?appilix_push_notification_user_identity='+resData.clientId);
              break;
          }
          
        } else {
          setError(resData.message || 'Login failed. Please check your credentials.');
        }
      
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!username.trim()) {
      errors.username = 'User ID is required';
    } else if (username.length < 3) {
      errors.username = 'User ID must be at least 3 characters';
    }
    
    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 4) {
      errors.password = 'Password must be at least 4 characters';
    }
    
    setFieldErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);
    return isValid;
  };

  // Handle input changes with validation
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setError('');
    if (fieldErrors.username) {
      setFieldErrors(prev => ({ ...prev, username: '' }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
    if (fieldErrors.password) {
      setFieldErrors(prev => ({ ...prev, password: '' }));
    }
  };

  useEffect(() => {
    setHostname(window.location.hostname);
    // Only validate if user has started typing
    if (username.length > 0 || password.length > 0) {
      validateForm();
    }
  }, [username, password]);

  const getLogoImage = () => {
    if (hostnamee.includes('suryanx')) {
      return suryanx_icon;
    } 
    // Default image if no specific domain matches
    return suryanx_icon;
  };

  return token ? (
    <Navigate to="/" />
  ) : (
    <div className={`login-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Background Elements */}
      <div className="login-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <Container maxWidth="sd" className="login-wrapper">
        <Box className="login-content">
          {/* Header Section */}
          <Box className="login-header">
            <div className="logo-container">
              <img 
                src={getLogoImage()} 
                alt="Company Logo" 
                className="company-logo"
              />
              <div className="logo-glow"></div>
            </div>
            <Typography variant="h3" className="login-title">
              Welcome Back
            </Typography>
            <Typography variant="h6" className="login-subtitle">
              Sign in to access your trading dashboard
            </Typography>
          </Box>

          {/* Main Login Card */}
          <Card className="login-card">
            <CardContent className="login-card-content">
              {/* Card Header */}
              
              {/* Login Form */}
              <form onSubmit={handleSubmit} className="login-form">
                {/* Error Alert */}
                {error && (
                  <Alert 
                    severity="error" 
                    className="error-alert"
                    icon={<ErrorOutline />}
                  >
                    {error}
                  </Alert>
                )}

                {/* Username Field */}
                <Box className="field-container">
                  <TextField
                    fullWidth
                    label="User ID"
                    variant="outlined"
                    value={username}
                    onChange={handleUsernameChange}
                    required
                    className={`login-field ${fieldErrors.username ? 'error' : ''}`}
                    error={!!fieldErrors.username}
                    helperText={fieldErrors.username}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person className="field-icon" />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isLoading}
                  />
                </Box>

                {/* Password Field */}
                <Box className="field-container">
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    className={`login-field ${fieldErrors.password ? 'error' : ''}`}
                    error={!!fieldErrors.password}
                    helperText={fieldErrors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock className="field-icon" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            disabled={isLoading}
                            className="password-toggle"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={isLoading}
                  />
                </Box>

                {/* Login Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  className={`login-button ${isFormValid ? 'valid' : 'invalid'}`}
                  disabled={isLoading || !isFormValid}
                  endIcon={isLoading ? <CircularProgress size={20} /> : <ArrowForward />}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              {/* Register Link */}
              {hostnamee.includes('learnfno') && (
                <Box className="register-section">
                  <Divider className="register-divider" />
                  <Typography variant="body2" className="register-text">
                    Don't have an account?{' '}
                    <Link to="/register" className="register-link">
                      Create one here
                    </Link>
                  </Typography>
                </Box>
              )}

              {/* Security Notice */}
              <Box className="security-notice">
                <CheckCircle className="security-check-icon" />
                <Typography variant="caption" className="security-text">
                  Your data is protected with enterprise-grade security
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </div>
  );
};

export default Login;
